import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import { Button } from '@mui/material';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productService } from 'shared/services';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { movementService } from '../../api/movement.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import {
  EnhancedDialog,
  AutocompleteAdapter,
  TextFieldAdapter,
  DatePickerAdapter
} from 'components/enhanced';

interface DeleteFilteredMovementsButtonProps {
  refresh?: any;
}

export function DeleteFilteredMovementsButton({ refresh }: DeleteFilteredMovementsButtonProps) {
  const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState(false);

  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<Option[]>([]);

  useEffect(() => {
    // Fetch product and warehouse options here
    productService.getAllBaseInformation().then(setProductOptions);
    warehouseService.getAllBaseInformation().then(setWarehouseOptions);
  }, []);

  const toggleOpenDialog = () => {
    setOpenDialog(!openDialog);
  };

  const onSubmit = (values: any) => {
    movementService.deleteByFilter(values).then((res) => {
      if (res.status === 200) {
        if (refresh) refresh?.current();
        toast.success(t('actions.successfully.multi-deleted'));
        setOpenDialog(false);
      } else {
        if (res.data?.warining) {
          toast.warning(res.data.warining);
        }
        if (res.data?.errors) {
          toast.error(res.data.errors);
        }
      }
    });
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={toggleOpenDialog}
        title={t('actions.delete-filtered')}
        sx={{
          backgroundColor: '#1d70b8',
          color: '#ffffff',
          minWidth: 0,
          width: 36,
          height: 36,
          padding: 0,
          borderRadius: 1.5,
          border: '1px solid #1d70b8',
          '&:hover': {
            backgroundColor: '#155a96',
            borderColor: '#155a96',
            boxShadow: 'none'
          },
          '& .MuiButton-startIcon': {
            fontSize: 'small',
            color: '#ffffff',
            marginRight: '6px'
          },
          '& .MuiTypography-root': {
            color: '#ffffff'
          }
        }}>
        <AutoDeleteIcon fontSize="small" />
      </Button>
      <EnhancedDialog
        title={t(`global.delete-confirmation`)}
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
        submitLabel={t('actions.delete')}
        submitButtonProps={{
          type: 'submit',
          form: 'deleteFilteredMovements'
        }}>
        <EnhancedForm
          onSubmit={onSubmit}
          formId="deleteFilteredMovements"
          hideControls
          autoStyleLayout
          columns={5}>
          <IdentityField
            name="product"
            component={AutocompleteAdapter}
            options={productOptions}
            label={t('global.product')}
          />
          <IdentityField
            name="warehouse"
            component={AutocompleteAdapter}
            options={warehouseOptions}
            label={t('global.warehouse')}
          />
          <IdentityField
            name="product_batch"
            component={TextFieldAdapter}
            label={t('entities.movement.product_batch')}
          />
          <IdentityField
            name="date_start"
            component={DatePickerAdapter}
            label={t('global.date_start')}
            type="date"
          />
          <IdentityField
            name="date_end"
            component={DatePickerAdapter}
            label={t('global.date_end')}
            type="date"
          />
        </EnhancedForm>
      </EnhancedDialog>
    </>
  );
}
